import { Lens } from '@dhmk/zustand-lens';

import { RegistryCheckStatus } from '@/utils/registry';

export type Shareholders = {
  type: string | null;
  email: string | null;
  fullName: string | null;
  numberOfShares: number | null;
};

export type ShareClasses = {
  class: string | null;
  numberOfShares: number | null;
  shareholders: Shareholders[] | null;
};

export type ShareStructure = {
  totalNumberOfShares: number | null;
  shareClasses: ShareClasses[];
};

export type StepForms = {
  id: string;
  isApproved: boolean;
  isLegalNameCorrect: boolean | null;
  isDisplayNameCorrect: boolean | null;
  lastValidatedLegalName: string;
  lastValidatedDisplayName: string;
  entityApprovalStatus: string;
  draftStatus: string;
  legalName: string | null;
  displayName: string | null;
  identificationNumber: string | null;
  country: string | null;
  state: string | null;
  type: string | null;
  logoImageId: string | null;
  logoUrl: string;
  registeredAt: string | Date | null;
  previewLogo: File | null;
  shareStructure: ShareStructure;
  entityEmail: string | null;
  entityWebsite: string | null;
  registeredAddressCity: string | null;
  registeredAddress: string | null;
  registeredAddressPostalCode: string | null;

  invitations: {
    id: string;
    email: string;
    title: string;
    role: 'Viewer' | 'Initiator' | 'Approver' | 'Admin';
    legalRole: string;
    status?: string;
    createdAt?: string;
  }[];

  members: {
    id: string;
    userId: string;
    title: string;
    role: 'Viewer' | 'Initiator' | 'Approver' | 'Admin';
    legalRole: string;
    createdAt?: string;
  }[];

  verifications: {
    id: string;
    url?: string;
    fileId?: string;
    type: string;
    format: 'url' | 'file';
    fileSize?: number;
    fileName?: string;
  }[];

  createdAt: string;
  updatedAt: string;
};

export type EntitySlice = {
  currentStep: number;
  stepForms: StepForms;
  savedStepForms: StepForms | null;
  validatedSteps: number[];
  isLogoUploading: boolean;
  registryLockedFields: string[];
  registryStatus: RegistryCheckStatus;
  setRegistryLockedFields: (_fields: string[]) => void;
  setRegistryStatus: (_status: RegistryCheckStatus) => void;
  setIsLogoUploading: (_v: boolean) => void;
  setStep: (_step: number) => void;
  updateStepForm: (_data: Partial<StepForms>) => void;
  approvedStepForms: StepForms | null;
  isViewingNewVersion: boolean;
  isPendingReview: boolean;
  changedFieldPaths: string[];
  changedCountByStep: Record<number, number>;
  changedDirectorIds: string[];
  approvedDirectorUserIds: string[];
  setEntityVersions: (_data: {
    approved: StepForms | null;
    isPendingReview: boolean;
    changedFieldPaths: string[];
    changedCountByStep: Record<number, number>;
    changedDirectorIds: string[];
    approvedDirectorUserIds: string[];
  }) => void;
  setIsViewingNewVersion: (_value: boolean) => void;
  resetState: () => void;
  markStepValidated: (_step: number) => void;
  hasUnsavedChanges: () => boolean;
  saveCurrentState: () => void;
};

const initialState: Omit<
  EntitySlice,
  | 'setStep'
  | 'resetState'
  | 'updateStepForm'
  | 'markStepValidated'
  | 'hasUnsavedChanges'
  | 'saveCurrentState'
  | 'setIsLogoUploading'
  | 'setRegistryLockedFields'
  | 'setRegistryStatus'
  | 'setEntityVersions'
  | 'setIsViewingNewVersion'
> = {
  isLogoUploading: false,
  currentStep: 1,
  savedStepForms: null,
  validatedSteps: [],
  registryLockedFields: [],
  registryStatus: 'idle',
  approvedStepForms: null,
  isViewingNewVersion: true,
  isPendingReview: false,
  changedFieldPaths: [],
  changedCountByStep: {},
  changedDirectorIds: [],
  approvedDirectorUserIds: [],
  stepForms: {
    entityApprovalStatus: '',
    id: '',
    isApproved: false,
    isLegalNameCorrect: null,
    isDisplayNameCorrect: null,
    lastValidatedLegalName: '',
    lastValidatedDisplayName: '',
    draftStatus: '',
    legalName: null,
    displayName: null,
    identificationNumber: null,
    country: null,
    state: null,
    type: null,
    logoImageId: null,
    logoUrl: '',
    registeredAt: null,
    previewLogo: null,
    entityEmail: null,
    entityWebsite: null,
    registeredAddressCity: null,
    registeredAddress: null,
    registeredAddressPostalCode: null,

    shareStructure: {
      totalNumberOfShares: null,
      shareClasses: [
        {
          class: null,
          numberOfShares: null,
          shareholders: [{ type: null, email: null, fullName: null, numberOfShares: null }],
        },
      ],
    },

    invitations: [],
    members: [],
    verifications: [],

    createdAt: '',
    updatedAt: '',
  },
};

export const createEntityStore: Lens<EntitySlice> = (set, get) => ({
  ...initialState,
  setStep: (step) =>
    set((state) => {
      if (state.stepForms.id) {
        state.savedStepForms = JSON.parse(JSON.stringify(state.stepForms));
      }
      state.currentStep = step;
    }),
  resetState: () =>
    set(() => {
      const stepForms = { ...initialState.stepForms };

      return {
        ...initialState,
        stepForms,
        savedStepForms: JSON.parse(JSON.stringify(stepForms)) as StepForms,
      };
    }),
  updateStepForm: (data) =>
    set((state) => {
      state.stepForms = {
        ...state.stepForms,
        ...data,
      };
    }),

  setEntityVersions: ({
    approved,
    isPendingReview,
    changedFieldPaths,
    changedCountByStep,
    changedDirectorIds,
    approvedDirectorUserIds,
  }) =>
    set((state) => {
      state.approvedStepForms = approved;
      state.isPendingReview = isPendingReview;
      state.changedFieldPaths = changedFieldPaths;
      state.changedCountByStep = changedCountByStep;
      state.changedDirectorIds = changedDirectorIds;
      state.approvedDirectorUserIds = approvedDirectorUserIds;
      if (!isPendingReview) {
        state.isViewingNewVersion = true;
      }
    }),

  setIsViewingNewVersion: (value) =>
    set((state) => {
      state.isViewingNewVersion = value;
    }),

  markStepValidated: (step: number) =>
    set((state) => ({
      validatedSteps: state.validatedSteps.includes(step)
        ? state.validatedSteps
        : [...state.validatedSteps, step],
    })),
  saveCurrentState: () =>
    set((state) => {
      state.savedStepForms = JSON.parse(JSON.stringify(state.stepForms));
    }),
  hasUnsavedChanges: () => {
    const state = get();
    if (!state.savedStepForms) return true;
    return JSON.stringify(state.stepForms) !== JSON.stringify(state.savedStepForms);
  },
  setIsLogoUploading: (v) => set((s) => ({ ...s, isLogoUploading: v })),
  setRegistryLockedFields: (fields) =>
    set((state) => {
      state.registryLockedFields = fields;
    }),
  setRegistryStatus: (status) =>
    set((state) => {
      state.registryStatus = status;
    }),
});
